/**
 * How somebody reached the editor.
 *
 * `landing_cta_clicked` fires on the page being left, one frame before a full
 * navigation, so it can only ever undercount: a click before React has mounted
 * over the prerendered page is a plain link and fires nothing, and a click
 * after it depends on the analytics SDK flushing during unload. This module
 * measures the same hop from the side that cannot lose it — the editor, once
 * it has booted.
 *
 * The CTA writes a handoff to `sessionStorage` (per tab, cleared on close) and
 * the editor takes it on boot. Without one, the referrer still says whether the
 * visitor came from one of our own pages, a search engine, or nowhere.
 *
 * **Leaf module, no imports.** `landing-gate.ts` is on the public pages' boot
 * path and `app-entry.tsx` on the editor's; neither wants a dependency for the
 * sake of one string.
 */

const KEY = "twelve-volt:canvas-entry";

/** A handoff older than this is a stale tab, not the click that brought us here. */
const MAX_AGE_MS = 10 * 60 * 1_000;

export type CanvasEntryHandoff = {
  /** The CTA's `source` (`hero`, `header`, `closing`, `component_hero`, …). */
  source: string;
  /** The public page the CTA was on. */
  fromPath: string;
  at: number;
};

export type CanvasEntry = {
  /** A CTA source when we own the click; otherwise how the visitor got here. */
  entry_source: string;
  /** Our own page the visitor came from, when the browser or a handoff says. */
  entry_from_path: string | null;
  /** The outside site that sent them straight to the editor, if any. */
  entry_referrer_domain: string | null;
};

export function markCanvasEntry(
  source: string,
  now: () => number = Date.now,
  fromPath: string | null = currentPath(),
): void {
  try {
    const handoff: CanvasEntryHandoff = {
      source,
      fromPath: fromPath ?? "",
      at: now(),
    };
    sessionStorage.setItem(KEY, JSON.stringify(handoff));
  } catch {
    // Private browsing or a full quota: the referrer fallback still applies.
  }
}

/** Read and clear the handoff, so a reload of the editor cannot count twice. */
export function takeCanvasEntry(
  now: () => number = Date.now,
): CanvasEntryHandoff | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    const parsed = JSON.parse(raw) as Partial<CanvasEntryHandoff>;
    if (typeof parsed?.source !== "string" || typeof parsed.at !== "number") {
      return null;
    }
    if (now() - parsed.at > MAX_AGE_MS) return null;
    return {
      source: parsed.source,
      fromPath: typeof parsed.fromPath === "string" ? parsed.fromPath : "",
      at: parsed.at,
    };
  } catch {
    return null;
  }
}

export function describeCanvasEntry(input: {
  handoff: CanvasEntryHandoff | null;
  referrer: string;
  origin: string;
}): CanvasEntry {
  if (input.handoff) {
    return {
      entry_source: input.handoff.source,
      entry_from_path: input.handoff.fromPath || null,
      entry_referrer_domain: null,
    };
  }

  const referrer = parseUrl(input.referrer);

  if (!referrer) {
    return {
      entry_source: "direct",
      entry_from_path: null,
      entry_referrer_domain: null,
    };
  }

  if (referrer.origin === input.origin) {
    // A click we did not see: the prerendered page before React mounted, a
    // middle-click, a link in the footer that is not a CTA.
    return {
      entry_source: "internal_link",
      entry_from_path: referrer.pathname,
      entry_referrer_domain: null,
    };
  }

  return {
    entry_source: "external",
    entry_from_path: null,
    entry_referrer_domain: referrer.hostname,
  };
}

function parseUrl(value: string): URL | null {
  if (!value) return null;
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function currentPath(): string | null {
  if (typeof location === "undefined") return null;
  return location.pathname;
}
